import * as CSS from 'csstype'
import {CSSProperties} from 'react'
import {
  type TranslationKey,
  type ValidSystemTheme,
  hexToRgb,
  systemThemeName
} from 'notability-services-common-universal'
import {shadowBlurRadiusLevel1} from '../constants'

export const accentThemeColorNames = [
  'base', // Primary action color
  'hover', // Hover
  'press', // Press/active - darkest
  'container', // Lightest tint
  'containerHover', // Medium tint
  'containerPress' // Darkest tint
] as const
export const coreThemeColorNames = [
  /** Primary foreground color (e.g. 'black' for light theme, 'white' for dark theme) */
  'f1', // Primary
  'f2', // Secondary
  'f3', // Tertiary
  'f4', // Muted
  'onAccent', // Text over accent

  /** Primary background color (e.g. 'white' for light theme, 'black' for dark theme) */
  'b1', // Primary
  'b2', // Secondary
  'b3', // Tertiary
  'b4' // Muted/Divider
] as const

export const accentThemeNames = ['accent', 'neutral', 'secondary', 'alert', 'success'] as const

export type AccentThemeType = (typeof accentThemeNames)[number]
export type CoreThemeColor = (typeof coreThemeColorNames)[number]
export type AccentThemeColor = (typeof accentThemeColorNames)[number]

// A Theme is a collection of visual styles with well-defined meanings
export type CoreTheme = Record<CoreThemeColor, CSS.Property.Color>
export type AccentTheme = Record<AccentThemeColor, CSS.Property.Color>
export const themeSettings = [...systemThemeName, 'auto'] as const
export type ThemeSetting = (typeof themeSettings)[number] | 'custom'
export type SubThemeType = AccentThemeType | 'core' | 'elevation'
export type Theme = Record<AccentThemeType, AccentTheme> & {core: CoreTheme} & {
  elevation: {
    level1: CSSProperties
    level2: CSSProperties
    level3: CSSProperties
  }
}

export const mobileThemeNameMap: {[key: string]: ValidSystemTheme} = {
  Light: 'light',
  'Very Dark': 'dark',
  Dark: 'darkBlue',
  Fall: 'fall',
  Summer: 'summer',
  Spring: 'spring',
  Winter: 'winter',
  Grape: 'grape',
  Strawberry: 'strawberry',
  Mint: 'mint',
  JetBlack: 'jetBlack',
  JetBlackSpooky: 'jetBlackSpooky',
  Halloween: 'halloween',
  'Botanical Day': 'botanicalDay',
  'Botanical Night': 'botanicalNight'
} as const

function themeCheck<A extends {[key in ValidSystemTheme]: Theme}>(themeCreator: () => A): A {
  return themeCreator()
}

const DarkThemeNames: ReadonlyArray<ValidSystemTheme> = [
  'botanicalNight',
  'dark',
  'darkBlue',
  'jetBlack',
  'jetBlackSpooky',
  'halloween'
] as const
export function themeIsDark(themeName: ValidSystemTheme | 'custom') {
  return themeName !== 'custom' && DarkThemeNames.includes(themeName)
}

// constant color values that are universal across themes or re-used in one theme or another under a different name with a different purpose
const f2Light: CSS.Property.Color = '#444F60'
const f2Dark: CSS.Property.Color = '#F5F6F8'
const b3Light: CSS.Property.Color = '#E8EBF0'
const b3Dark: CSS.Property.Color = '#404856'

export const themeDefinitions: {[key in ValidSystemTheme]: Theme} = {
  light: {
    core: {
      f1: '#171A20',
      f2: f2Light,
      f3: '#7183A0',
      f4: '#AAB5C6',
      onAccent: '#FFFFFF',

      b1: '#ffffff',
      b2: '#F6F6F8',
      b3: b3Light,
      b4: '#C8CFDB'
    },
    accent: {
      base: '#4278FF',
      hover: '#3560CC',
      press: '#284899',
      container: '#ECF2FF',
      containerHover: '#D9E4FF',
      containerPress: '#B3C9FF'
    },
    neutral: {
      base: f2Light,
      hover: '#363F4D',
      press: '#292F3A',
      container: b3Light,
      containerHover: '#D4DAE3',
      containerPress: '#B9C2D1'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('light')),
    elevation: elevationForTheme(themeIsDark('light'), '#171A20')
  },
  dark: {
    core: {
      f1: '#FFFFFF',
      f2: f2Dark,
      f3: '#D4DAE3',
      f4: '#A7B3C6',
      onAccent: '#0C0D11',

      b1: '#0C0D11',
      b2: '#1F242B',
      b3: b3Dark,
      b4: '#616B7D'
    },
    accent: {
      base: '#5BA8F5',
      hover: '#7CB9F7',
      press: '#9DCBF9',
      container: '#122231',
      containerHover: '#244362',
      containerPress: '#376593'
    },
    neutral: {
      base: f2Dark,
      hover: '#F5F6F8',
      press: '#FFFFFF',
      container: b3Dark,
      containerHover: '#4C5565',
      containerPress: '#67707E'
    },
    secondary: {
      base: '#FFA629',
      hover: '#FFB854',
      press: '#FFCA7F',
      container: '#332108',
      containerHover: '#664210',
      containerPress: '#996419'
    },
    alert: {
      base: '#FF577E',
      hover: '#FF7998',
      press: '#FF9AB2',
      container: '#331119',
      containerHover: '#662332',
      containerPress: '#99344C'
    },
    success: successForTheme(themeIsDark('dark')),
    elevation: elevationForTheme(themeIsDark('dark'), '#FFFFFF')
  },
  darkBlue: {
    core: {
      f1: '#FFFFFF',
      f2: '#F0F2F7',
      f3: '#D6DDE9',
      f4: '#ADBBD3',
      onAccent: '#0A0D11',

      b1: '#0A0D11',
      b2: '#1B222E',
      b3: '#36445C',
      b4: '#51668A'
    },
    accent: {
      base: '#5BA8F5',
      hover: '#7CB9F7',
      press: '#9DCBF9',
      container: '#122231',
      containerHover: '#244362',
      containerPress: '#376593'
    },
    neutral: {
      base: '#D6DDE9',
      hover: '#F0F2F7',
      press: '#FFFFFF',
      container: '#1B222E',
      containerHover: '#36445C',
      containerPress: '#51668A'
    },
    secondary: {
      base: '#FFA629',
      hover: '#FFB854',
      press: '#FFCA7F',
      container: '#332108',
      containerHover: '#664210',
      containerPress: '#996419'
    },
    alert: {
      base: '#FF577E',
      hover: '#FF7998',
      press: '#FF9AB2',
      container: '#331119',
      containerHover: '#662332',
      containerPress: '#99344C'
    },
    success: successForTheme(themeIsDark('darkBlue')),
    elevation: elevationForTheme(themeIsDark('darkBlue'), '#FFFFFF')
  },
  jetBlack: {
    core: {
      f1: '#FEFEFE',
      f2: '#D9D9D9',
      f3: '#B4B4B4',
      f4: '#919191',
      onAccent: '#000000',

      b1: '#000000',
      b2: '#151515',
      b3: '#222222',
      b4: '#6F6F6F'
    },
    accent: {
      base: '#5BA8F5',
      hover: '#7CB9F7',
      press: '#9DCBF9',
      container: '#122231',
      containerHover: '#244362',
      containerPress: '#376593'
    },
    neutral: {
      base: '#C6C6C6',
      hover: '#ECECEC',
      press: '#FFFFFF',
      container: '#222222',
      containerHover: '#3F3F3F',
      containerPress: '#5E5E5E'
    },
    secondary: {
      base: '#FFA629',
      hover: '#FFB854',
      press: '#FFCA7F',
      container: '#332108',
      containerHover: '#664210',
      containerPress: '#996419'
    },
    alert: {
      base: '#FF577E',
      hover: '#FF7998',
      press: '#FF9AB2',
      container: '#331119',
      containerHover: '#662332',
      containerPress: '#99344C'
    },
    success: successForTheme(themeIsDark('jetBlack')),
    elevation: elevationForTheme(themeIsDark('jetBlack'), '#FEFEFE')
  },
  jetBlackSpooky: {
    core: {
      f1: '#FEFEFE',
      f2: '#D9D9D9',
      f3: '#B4B4B4',
      f4: '#919191',
      onAccent: '#000000',

      b1: '#000000',
      b2: '#151515',
      b3: '#222222',
      b4: '#6F6F6F'
    },
    accent: {
      base: '#FF5823',
      hover: '#FF794F',
      press: '#FF9B7B',
      container: '#331207',
      containerHover: '#66230E',
      containerPress: '#993515'
    },
    neutral: {
      base: '#C6C6C6',
      hover: '#ECECEC',
      press: '#FFFFFF',
      container: '#222222',
      containerHover: '#3F3F3F',
      containerPress: '#5E5E5E'
    },
    secondary: {
      base: '#FFA629',
      hover: '#FFB854',
      press: '#FFCA7F',
      container: '#332108',
      containerHover: '#664210',
      containerPress: '#996419'
    },
    alert: {
      base: '#FF577E',
      hover: '#FF7998',
      press: '#FF9AB2',
      container: '#331119',
      containerHover: '#662332',
      containerPress: '#99344C'
    },
    success: successForTheme(themeIsDark('jetBlackSpooky')),
    elevation: elevationForTheme(themeIsDark('jetBlackSpooky'), '#FEFEFE')
  },
  halloween: {
    core: {
      f1: '#FFFFFF',
      f2: '#ECEAEE',
      f3: '#D8D5DE',
      f4: '#B2ACBC',
      onAccent: '#191323',

      b1: '#191323',
      b2: '#322646',
      b3: '#655979',
      b4: '#B2ACBC'
    },
    accent: {
      base: '#32E38E',
      hover: '#5BE9A5',
      press: '#84EEBB',
      container: '#0A2D1C',
      containerHover: '#145B39',
      containerPress: '#1E8855'
    },
    neutral: {
      base: '#B2ACBC',
      hover: '#D8D5DE',
      press: '#FFFFFF',
      container: '#3E2F58',
      containerHover: '#655979',
      containerPress: '#8B829B'
    },
    secondary: {
      base: '#FFA629',
      hover: '#FFB854',
      press: '#FFCA7F',
      container: '#332108',
      containerHover: '#664210',
      containerPress: '#996419'
    },
    alert: {
      base: '#FF5823',
      hover: '#FF794F',
      press: '#FF9B7B',
      container: '#331207',
      containerHover: '#66230E',
      containerPress: '#993515'
    },
    success: successForTheme(themeIsDark('halloween')),
    elevation: elevationForTheme(themeIsDark('halloween'), '#FFFFFF')
  },
  grape: {
    core: {
      f1: '#171418',
      f2: '#443B49',
      f3: '#716379',
      f4: '#AAA1AF',

      onAccent: '#FFFFFF',
      b1: '#FDFDFD',
      b2: '#F1EEF3',
      b3: '#E3DEE6',
      b4: '#C7BCCE'
    },
    accent: {
      base: '#7C36A6',
      hover: '#632B85',
      press: '#4A2064',
      container: '#F2EBF6',
      containerHover: '#E5D7ED',
      containerPress: '#CBAFDB'
    },
    neutral: {
      base: '#665A6E',
      hover: '#554B5B',
      press: '#453C4A',
      container: '#DBD8DD',
      containerHover: '#CAC5CD',
      containerPress: '#B9B2BD'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('grape')),
    elevation: elevationForTheme(themeIsDark('grape'), '#171418')
  },
  mint: {
    core: {
      f1: '#162020',
      f2: '#2B413F',
      f3: '#5E7472',
      f4: '#AFB9B9',
      onAccent: '#FFFFFF',

      b1: '#FDFEFE',
      b2: '#EFF1F1',
      b3: '#DFE3E3',
      b4: '#C0C8C8'
    },
    accent: {
      base: '#00877A',
      hover: '#006C62',
      press: '#005149',
      container: '#E6F3F2',
      containerHover: '#CCE7E4',
      containerPress: '#99CFCA'
    },
    neutral: {
      base: '#5E7471',
      hover: '#36514E',
      press: '#2B413E',
      container: '#EBEEED',
      containerHover: '#D7DCDC',
      containerPress: '#AFB9B8'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('mint')),
    elevation: elevationForTheme(themeIsDark('mint'), '#162020')
  },
  strawberry: {
    core: {
      f1: '#191114',
      f2: '#4B323D',
      f3: '#7D5365',
      f4: '#B198A3',
      onAccent: '#FFFFFF',

      b1: '#FFFDFE',
      b2: '#F2EEF0',
      b3: '#E5DDE0',
      b4: '#CBBAC2'
    },
    accent: {
      base: '#D00057',
      hover: '#A60046',
      press: '#7D0034',
      container: '#FAE6EE',
      containerHover: '#F6CCDD',
      containerPress: '#EC99BC'
    },
    neutral: {
      base: '#874965',
      hover: '#6C3A51',
      press: '#512C3D',
      container: '#F3EDF0',
      containerHover: '#E7DBE0',
      containerPress: '#CFB6C1'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('strawberry')),
    elevation: elevationForTheme(themeIsDark('strawberry'), '#191114')
  },
  spring: {
    core: {
      f1: '#0E110B',
      f2: '#1D2216',
      f3: '#505549',
      f4: '#A7AAA4',
      onAccent: '#FFFFFF',

      b1: '#F9FAF9',
      b2: '#E9EBE7',
      b3: '#E2E5DE',
      b4: '#C5CBBE'
    },
    accent: {
      base: '#243B0C',
      hover: '#1D2F0A',
      press: '#162307',
      container: '#E9EBE7',
      containerHover: '#D3D8CE',
      containerPress: '#A7B19E'
    },
    neutral: {
      base: '#50524D',
      hover: '#242720',
      press: '#1D1F1A',
      container: '#E9E9E9',
      containerHover: '#D6DADB',
      containerPress: '#ADB5B7'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('spring')),
    elevation: elevationForTheme(themeIsDark('spring'), '#0E110B')
  },
  summer: {
    core: {
      f1: '#1A1511',
      f2: '#4D3E34',
      f3: '#806756',
      f4: '#B3A49A',
      onAccent: '#FFFFFF',

      b1: '#FCFBFB',
      b2: '#F4F0EE',
      b3: '#F0EBE8',
      b4: '#E2D7D1'
    },
    accent: {
      base: '#D35803',
      hover: '#A94602',
      press: '#7F3502',
      container: '#FBEEE6',
      containerHover: '#F6DECD',
      containerPress: '#EDBC9A'
    },
    neutral: {
      base: '#785741',
      hover: '#5A4231',
      press: '#3E2D21',
      container: '#F4F0ED',
      containerHover: '#E8E1DC',
      containerPress: '#D2C4BA'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('summer')),
    elevation: elevationForTheme(themeIsDark('summer'), '#1A1511')
  },
  fall: {
    core: {
      f1: '#2D2625',
      f2: '#514442',
      f3: '#89807F',
      f4: '#B5AFAE',
      onAccent: '#FFFFFF',

      b1: '#FFFFFE',
      b2: '#FBF6F5',
      b3: '#F2E1DE',
      b4: '#EACFCB'
    },
    accent: {
      base: '#7A1400',
      hover: '#621000',
      press: '#490C00',
      container: '#F2E8E6',
      containerHover: '#E4D0CC',
      containerPress: '#CAA199'
    },
    neutral: {
      base: '#8E7C79',
      hover: '#593E3B',
      press: '#312321',
      container: '#F6F5F4',
      containerHover: '#EAE7E6',
      containerPress: '#D6CFCE'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('fall')),
    elevation: elevationForTheme(themeIsDark('fall'), '#2D2625')
  },
  winter: {
    core: {
      f1: '#141C1E',
      f2: '#27383B',
      f3: '#5A6B6E',
      f4: '#ADB5B7',
      onAccent: '#FFFFFF',

      b1: '#FBFBFC',
      b2: '#EFF1F2',
      b3: '#E9ECED',
      b4: '#D4DADB'
    },
    accent: {
      base: '#00667B',
      hover: '#005262',
      press: '#003D4A',
      container: '#E6F0F2',
      containerHover: '#CCE0E5',
      containerPress: '#99C2CA'
    },
    neutral: {
      base: '#31464A',
      hover: '#27383B',
      press: '#1D2A2C',
      container: '#EAEDED',
      containerHover: '#D6DADB',
      containerPress: '#ADB5B7'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#FF577E',
      hover: '#CC4665',
      press: '#99344C',
      container: '#FFEEF2',
      containerHover: '#FFDDE5',
      containerPress: '#FFBCCB'
    },
    success: successForTheme(themeIsDark('winter')),
    elevation: elevationForTheme(themeIsDark('winter'), '#141C1E')
  },
  botanicalDay: {
    core: {
      f1: '#2B3430',
      f2: '#3D4A44',
      f3: '#576960',
      f4: '#6A8176',
      onAccent: '#FFFFFF',

      b1: '#FAFBFA',
      b2: '#F2FAEE',
      b3: '#E3F5DB',
      b4: '#D5EDCA'
    },
    accent: {
      base: '#03AB8C',
      hover: '#72BFA9',
      press: '#A1D1C2',
      container: '#F2F8F6',
      containerHover: '#E4F1ED',
      containerPress: '#C6E1D8'
    },
    neutral: {
      base: '#89A698',
      hover: '#576960',
      press: '#3D4A44',
      container: '#ECF0EE',
      containerHover: '#D8E0DC',
      containerPress: '#C1CEC8'
    },
    secondary: {
      base: '#FFA629',
      hover: '#CC8521',
      press: '#996419',
      container: '#FFF6EA',
      containerHover: '#FFEDD4',
      containerPress: '#FFDBA9'
    },
    alert: {
      base: '#ED691E',
      hover: '#D45E1B',
      press: '#B85117',
      container: '#FCE9E4',
      containerHover: '#F8D0C6',
      containerPress: '#F4B5A3'
    },
    success: successForTheme(themeIsDark('botanicalDay')),
    elevation: elevationForTheme(themeIsDark('botanicalDay'), '#2B3430')
  },
  botanicalNight: {
    core: {
      f1: '#FFFFFF',
      f2: '#E4E6EB',
      f3: '#C6CAD6',
      f4: '#A2AABE',
      onAccent: '#000304',

      b1: '#161638',
      b2: '#262661',
      b3: '#363588',
      b4: '#60608D'
    },
    accent: {
      base: '#1C97E9',
      hover: '#66B0F7',
      press: '#A1CAFA',
      container: '#07263A',
      containerHover: '#0A334F',
      containerPress: '#177CC0'
    },
    neutral: {
      base: '#A2AABE',
      hover: '#C6CAD6',
      press: '#FFFFFF',
      container: '#252934',
      containerHover: '#3A4152',
      containerPress: '#64718D'
    },
    secondary: {
      base: '#3CDCD2',
      hover: '#7EE3DC',
      press: '#A8EBE5',
      container: '#134642',
      containerHover: '#268B85',
      containerPress: '#36C5BC'
    },
    alert: {
      base: '#ED1E79',
      hover: '#F1759D',
      press: '#F4A3BB',
      container: '#4B0926',
      containerHover: '#6A0D36',
      containerPress: '#96134D'
    },
    success: successForTheme(themeIsDark('botanicalNight')),
    elevation: elevationForTheme(themeIsDark('botanicalNight'), '#FFFFFF')
  }
} as const

export const themes = themeCheck(() => themeDefinitions)

/**
 * Generate the elevation settings per theme
 *
 * @param isDark
 * @param accentColor Theme appropriate accent color. Use theme.f1 unless directed otherwise.
 * @returns
 */
function elevationForTheme(isDark: boolean, accentColor: string) {
  return isDark
    ? {
        level1: {
          border: `1px solid ${hexToRgb(accentColor, 0.24)}`
        },
        level2: {
          border: `1px solid ${hexToRgb(accentColor, 0.32)}`
        },
        level3: {
          border: `1px solid ${hexToRgb(accentColor, 0.4)}`
        }
      }
    : {
        level1: {
          boxShadow: `0px 1px ${shadowBlurRadiusLevel1}px 0px ${hexToRgb(accentColor, 0.14)}`
        },
        level2: {
          boxShadow: `0px 3px 10px 0px ${hexToRgb(accentColor, 0.14)}`
        },
        level3: {
          boxShadow: `0px 6px 12px 0px ${hexToRgb(accentColor, 0.14)}`
        }
      }
}

/**
 * Generate the success settings per theme
 *
 * @param isDark
 * @returns
 */
function successForTheme(isDark: boolean) {
  return isDark
    ? {
        base: '#026A57',
        hover: '#02856D',
        press: '#03AB8C',
        container: '#C6E1D8',
        containerHover: '#E4F1ED',
        containerPress: '#F2F8F6'
      }
    : {
        base: '#03AB8C',
        hover: '#02856D',
        press: '#026A57',
        container: '#F2F8F6',
        containerHover: '#E4F1ED',
        containerPress: '#C6E1D8'
      }
}

export const defaultThemeName: ValidSystemTheme = 'light'
export const defaultTheme: Theme = themes[defaultThemeName]
export const themeDisplayNames: Record<ThemeSetting, TranslationKey> = {
  light: 'colorSchemeLight',
  dark: 'colorSchemeDark',
  darkBlue: 'colorSchemeDarkBlue',
  jetBlack: 'colorSchemeJetBlack',
  jetBlackSpooky: 'colorSchemeJetBlackSpooky',
  halloween: 'colorSchemeHalloween',
  grape: 'colorSchemeGrape',
  mint: 'colorSchemeMint',
  strawberry: 'colorSchemeStrawberry',
  spring: 'colorSchemeSpring',
  summer: 'colorSchemeSummer',
  fall: 'colorSchemeFall',
  winter: 'colorSchemeWinter',
  botanicalDay: 'colorSchemeBotanicalDay',
  botanicalNight: 'colorSchemeBotanicalNight',
  custom: 'colorSchemeCustom',
  auto: 'colorSchemeAuto'
}

export const disabledAlpha = 0.32
